import { GeoLocation, LocationCheckResult } from '@/types/attendance';
import { ATTENDANCE_CONFIG } from '@/config/attendanceConfig';

/**
 * Calculate distance between two geographic coordinates using Haversine formula
 * @param lat1 Latitude of first point
 * @param lon1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lon2 Longitude of second point
 * @returns Distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Check if user location is within allowed range of office location
 * @param userLocation User's current location
 * @param officeLocation Office location coordinates
 * @param allowedRadius Allowed radius in meters (default: 100m)
 * @returns LocationCheckResult with validation details
 */
export function isWithinOfficeRange(
  userLocation: GeoLocation,
  officeLocation: GeoLocation,
  allowedRadius: number = ATTENDANCE_CONFIG.officeRadius
): LocationCheckResult {
  const distance = calculateDistance(
    userLocation.latitude,
    userLocation.longitude,
    officeLocation.latitude,
    officeLocation.longitude
  );

  return {
    isWithinRange: distance <= allowedRadius,
    distance: Math.round(distance), // Round to nearest meter
    officeLocation,
    userLocation,
    allowedRadius,
  };
}

/**
 * Get current user location using browser's Geolocation API with retry logic
 * @param timeout Timeout in milliseconds (default: 15000ms - increased from 10s)
 * @param maxRetries Maximum retry attempts (default: 2)
 * @param maxAccuracy Maximum acceptable accuracy in meters (default: 100m)
 * @returns Promise resolving to GeoLocation or rejecting with error
 */
export function getCurrentLocation(
  timeout: number = 15000,
  maxRetries: number = 2,
  maxAccuracy: number = 100
): Promise<GeoLocation> {
  let watchId: number | null = null; // Track watch ID for cleanup (Issue 20)
  let attempts = 0;

  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    const attemptLocation = () => {
      attempts++;
      
      // Try high accuracy first, then fall back to lower accuracy (Issue 3)
      const enableHighAccuracy = attempts === 1;
      
      watchId = navigator.geolocation.getCurrentPosition(
        (position) => {
          // Validate location accuracy (Issue 9)
          if (position.coords.accuracy > maxAccuracy) {
            if (attempts < maxRetries) {
              console.warn(`Location accuracy too low (${position.coords.accuracy}m), retrying...`);
              attemptLocation();
              return;
            }
            // Accept with warning on final attempt
            console.warn(`Using location with low accuracy: ${position.coords.accuracy}m`);
          }

          // Cleanup watch (Issue 20)
          if (watchId !== null) {
            navigator.geolocation.clearWatch(watchId);
          }

          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          });
        },
        (error) => {
          // Cleanup watch on error (Issue 20)
          if (watchId !== null) {
            navigator.geolocation.clearWatch(watchId);
          }

          if (attempts < maxRetries && error.code === error.TIMEOUT) {
            console.warn(`GPS timeout (attempt ${attempts}/${maxRetries}), retrying...`);
            attemptLocation();
            return;
          }

          let errorMessage = 'Unable to retrieve location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable. Please check your device GPS settings.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please ensure GPS is enabled and try again.';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy, // Start with high accuracy, fall back on retry
          timeout,
          maximumAge: attempts > 1 ? 5000 : 0, // Allow recent cache on retry
        }
      );
    };

    attemptLocation();
  });
}

/**
 * Format distance in human-readable format
 * @param meters Distance in meters
 * @returns Formatted string (e.g., "50m", "1.2km")
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

/**
 * Check if current time is late based on shift settings
 * @param shiftStartTime Shift start time in "HH:mm" format
 * @param gracePeriodMinutes Grace period in minutes
 * @param lateThresholdMinutes Late threshold in minutes after grace period
 * @returns Object with isLate and minutesLate
 */
export function checkIfLate(
  shiftStartTime: string = ATTENDANCE_CONFIG.shiftStartTime,
  gracePeriodMinutes: number = ATTENDANCE_CONFIG.gracePeriodMinutes,
  lateThresholdMinutes: number = ATTENDANCE_CONFIG.lateThresholdMinutes
): { isLate: boolean; minutesLate: number } {
  const now = new Date();
  const [hours, minutes] = shiftStartTime.split(':').map(Number);
  
  const shiftStart = new Date();
  shiftStart.setHours(hours, minutes, 0, 0);
  
  // Add grace period
  const graceEnd = new Date(shiftStart.getTime() + gracePeriodMinutes * 60000);
  
  if (now <= graceEnd) {
    return { isLate: false, minutesLate: 0 };
  }
  
  const minutesLate = Math.floor((now.getTime() - shiftStart.getTime()) / 60000);
  
  return {
    isLate: minutesLate > gracePeriodMinutes,
    minutesLate: Math.max(0, minutesLate - gracePeriodMinutes),
  };
}

/**
 * Format check-in time with late indicator
 * @param checkInTime Check-in timestamp
 * @param isLate Whether check-in was late
 * @param minutesLate Minutes late (if applicable)
 * @returns Formatted string
 */
export function formatCheckInTime(
  checkInTime: Date,
  isLate: boolean,
  minutesLate?: number
): string {
  const timeStr = checkInTime.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
  
  if (isLate && minutesLate) {
    return `${timeStr} (${minutesLate}min late)`;
  }
  
  return timeStr;
}

/**
 * Get color code for distance status
 * @param distance Distance from office in meters
 * @param allowedRadius Allowed radius in meters
 * @returns Tailwind color class
 */
export function getDistanceStatusColor(
  distance: number,
  allowedRadius: number
): string {
  if (distance <= allowedRadius) {
    return 'text-green-600';
  } else if (distance <= allowedRadius * 1.5) {
    return 'text-yellow-600';
  } else {
    return 'text-red-600';
  }
}
